// ============================================================================
// GROUPING WRAPPER - MAIN COMPONENT
// ============================================================================

import React from "react";
import {
  BaseEntity,
  TableFilters,
} from "@/components/shared/UniversalCRMTable/types/shared";
import { useGroupingWrapperData } from "./useGroupingWrapperData";
import { useGroupingState } from "./useGroupingState";
import { useGroupingPageContext } from "./useGroupingPageContext";
import { GroupingWrapperContent } from "./GroupingWrapperContent";
import type { GroupingWrapperProps } from "./types";

/**
 * Stable fallbacks for the props-path callers that omit these (the mobile demo,
 * any bare `<GroupingWrapper config data>`). A fresh `() => {}` / `{}` per render
 * is handed straight to the `React.memo`'d <HorizontalCardView> as `onEdit` /
 * `onView`, which defeats the memo exactly the way `visibleColumns` did.
 * See MEMO_STABLE_PROPS.md.
 */
const NOOP = (): void => {};
const EMPTY_FILTERS: TableFilters = {};

/**
 * GroupingWrapper - HOC that manages all grouping logic
 */
export function GroupingWrapper<T extends BaseEntity>(props: GroupingWrapperProps<T>) {
  const wrappedData = useGroupingWrapperData(props);
  const finalConfig = wrappedData.config;

  // `?? []` inline would allocate a NEW empty array on every render, which is a
  // dependency of every memo in useGroupingState — none of them would ever hold.
  const rawData = wrappedData.data;
  const finalData = React.useMemo(() => rawData ?? [], [rawData]);

  // Every hook runs unconditionally, ABOVE the `!finalConfig` bail-out below.
  // They used to sit after it, so the render where `config` finally arrived ran
  // more hooks than the one before it → "rendered fewer hooks than expected".
  const { groupableFields, processedData, groupingContext, groupByField, dualGrouping } =
    useGroupingState({ data: finalData, config: finalConfig });

  const { viewMode, appUser, BoardViewComponent } = useGroupingPageContext();

  if (!finalConfig) {
    console.error("GroupingWrapper: config is required (either from context or props)");
    return null;
  }

  return (
    <GroupingWrapperContent
      viewMode={viewMode}
      BoardViewComponent={BoardViewComponent}
      appUser={appUser}
      data={finalData}
      config={finalConfig}
      processedData={processedData}
      groupingContext={groupingContext}
      groupableFields={groupableFields}
      groupByField={groupByField}
      dualGrouping={dualGrouping}
      searchTerm={wrappedData.searchTerm ?? ""}
      setSearchTerm={wrappedData.setSearchTerm ?? NOOP}
      filters={wrappedData.filters ?? EMPTY_FILTERS}
      setFilters={wrappedData.setFilters ?? NOOP}
      onExport={wrappedData.onExport ?? NOOP}
      onEdit={wrappedData.onEdit ?? NOOP}
      onView={wrappedData.onView ?? NOOP}
      onOpenDetailModal={wrappedData.onOpenDetailModal}
      hooks={wrappedData.hooks}
      hasNextPage={wrappedData.hasNextPage ?? false}
      isFetchingNextPage={wrappedData.isFetchingNextPage ?? false}
      onLoadMore={wrappedData.onLoadMore}
    >
      {props.children}
    </GroupingWrapperContent>
  );
}
